import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/packages/paste-website/src/layouts/DefaultLayout.tsx";
import { graphql } from 'gatsby';
import { Box } from '@twilio-paste/box';
import Changelog from '@twilio-paste/box/CHANGELOG.md';
import { SidebarCategoryRoutes } from '../../../constants';
import { Callout, CalloutText } from '../../../components/callout';
import { DoDont, Do, Dont } from '../../../components/DoDont';
export const pageQuery = graphql`
  {
    allPastePrimitive(filter: {name: {eq: "@twilio-paste/box"}}) {
      edges {
        node {
          name
          description
          status
          version
        }
      }
    }
    mdx(fields: {slug: {eq: "/primitives/box/"}}) {
      fileAbsolutePath
      frontmatter {
        slug
        title
      }
      headings {
        depth
        value
      }
    }
  }
`;
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const ComponentHeader = makeShortcode("ComponentHeader");
const PageAside = makeShortcode("PageAside");
const LivePreview = makeShortcode("LivePreview");
const ChangelogRevealer = makeShortcode("ChangelogRevealer");
const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <ComponentHeader name="Box" categoryRoute={SidebarCategoryRoutes.PRIMITIVES} githubUrl="https://github.com/twilio-labs/paste/tree/main/packages/paste-core/primitives/box" storybookUrl="/?path=/story/primitives-box--default" data={props.data.allPastePrimitive.edges} mdxType="ComponentHeader" />
    <hr></hr>
    <contentwrapper>
      <PageAside data={props.data.mdx} mdxType="PageAside" />
      <content>
        <h2>{`Guidelines`}</h2>
        <p>{`The Box component is one of the pillars of our system. It can be any HTML element
and can receive many of our token values as props. We use Box to build most of
our other components.`}</p>
        <h3>{`About Box`}</h3>
        <p>{`All HTML elements can be considered as `}<strong parentName="p">{`boxes`}</strong>{`. In CSS, the term `}<strong parentName="p">{`"box model"`}</strong>{` is used when
talking about design and layout. It consists of: margins, borders, padding, and the actual content.`}</p>
        <p>{`The Box component is inspired from the “box model". It is a simple wrapper component
that takes tokens instead of using CSS. The limited but scoped set of tokens allowed
on Box act as guardrails. They provide quick and direct access to the Twilio design
language while preventing any other value. We prevent other values to protect our
product from inconsistent and inaccessible designs.`}</p>
        <p>{`For this reason, we can use the Box component to build out most of the interfaces at
Twilio. Using this component will make our products resilient to design changes and
themeable.`}</p>
        <p>{`The Box component takes original inspiration from `}<a parentName="p" {...{
            "href": "https://rebassjs.org/"
          }}>{`RebassJs`}</a>{` and is powered by `}<a parentName="p" {...{
            "href": "https://styled-system.com/"
          }}>{`Styled System`}</a>{`. The psuedo class functionality was inspired by and mimics `}<a parentName="p" {...{
            "href": "https://chakra-ui.com/"
          }}>{`Chakra UI`}</a>{`.`}</p>
        <h4>{`Accessibility`}</h4>
        <p>{`The Box component is an all purpose component. By default, it has no accessibility
concerns. If you use the Box as a custom element, it is up to you to manage the
resulting accessibility implications.`}</p>
        <Callout mdxType="Callout">
  <CalloutText mdxType="CalloutText">
    Be sure to use the <strong>as</strong> prop to select semantically meaningful HTML elements. By default the Box
    component renders as a <strong>div</strong> which doesn’t provide much meaning to the user.
  </CalloutText>
        </Callout>
        <h3>{`Examples`}</h3>
        <h4>{`Tokens as props`}</h4>
        <LivePreview scope={{
          Box
        }} language="jsx" mdxType="LivePreview">
  {`<Box
  as="article"
  backgroundColor="colorBackgroundBody"
  padding="space60"
>
Parent box on the hill side
  <Box
    backgroundColor="colorBackgroundSuccessLightest"
    display="inline-block"
    padding="space40"
  >
    nested box 1 made out of ticky tacky
  </Box>
  <Box
    backgroundColor="colorBackgroundPrimaryLight"
    display="inline-block"
    padding="space40"
  >
    Nested box 2 may look the same
  </Box>
</Box>`}
        </LivePreview>
        <h4>{`Tokens as pseudo-class props`}</h4>
        <p>{`To view the full list of pseudo-class props, check the `}<a parentName="p" {...{
            "href": "/primitives/box#props"
          }}>{`props table`}</a>{`.`}</p>
        <LivePreview scope={{
          Box
        }} language="jsx" mdxType="LivePreview">
  {`<Box
  backgroundColor="colorBackgroundPrimaryLight"
  padding="space60"
  transition="all 200ms ease-in"
  _hover={{padding: "space30", backgroundColor: "colorBackgroundPrimaryLightest", cursor: "pointer"}}
>
Hover over me!
</Box>`}
        </LivePreview>
        <h3>{`Composition Notes`}</h3>
        <p>{`The Box component is used to compose other experiences. If you imagine everything on
a webpage as a Box, you can see how they all come together. Let's assume we need to
build a modal component: it has a header, a body and a footer. Here's how we’ll
compose the modal using Box:`}</p>
        <LivePreview scope={{
          Box
        }} language="jsx" mdxType="LivePreview">
  {`<Box
  overflow="hidden"
  borderRadius="borderRadius20"
  borderStyle="solid"
  borderWidth="borderWidth10"
  borderColor="colorBorderPrimaryLight"
  >
  <Box backgroundColor="colorBackgroundPrimaryLight" padding="space40">
    Header area
  </Box>
  <Box backgroundColor="colorBackgroundPrimaryLightest" padding="space40">
    Body area
    <Box
      width="size20"
      marginTop="space30"
      marginBottom="space30"
      padding="space30"
    >
      An inner box with margin and padding
    </Box>
  </Box>
  <Box backgroundColor="colorBackgroundPrimaryLight" padding="space40">
    Footer area
  </Box>
</Box>`}
        </LivePreview>
        <h3>{`When to use Box`}</h3>
        <p>{`The Paste component library aims to cover the widest range of base use-cases.
Sometimes, product requirements are more specific than what is offered here.
When no other component in Paste works, use the Box component to build
new experiences.`}</p>
        <p>{`Be wary of building slightly different versions of existing Paste components.
If what you need is only a little different, try to constrain the design into
one of our provided solutions. This will allow you to leverage more of our
system and reduce your maintenance costs. On the surface our components may
look simple or restrictive. They are often limited for very specific reasons,
such as accessibility.`}</p>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Use Box to build new components and experiences." preview={false} mdxType="Do" />
  <Dont title="Don't" body="Use Box to build slightly different versions of existing Paste components." preview={false} mdxType="Dont" />
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Use Box to layout your page or to apply padding to your components." preview={false} mdxType="Do" />
  <Dont title="Don't" body="Use inline CSS, the CSS Cascade, or classNames to add styles that Box can provide." preview={false} mdxType="Dont" />
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Use Box with an 'as' prop to style custom HTML elements with tokens." preview={false} mdxType="Do" />
  <Dont title="Don't" body="Use HTML elements for functionality covered by the Box component." preview={false} mdxType="Dont" />
        </DoDont>
        <hr></hr>
        <h2>{`Usage Guide`}</h2>
        <h3>{`API`}</h3>
        <h4>{`Installation`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`yarn add @twilio-paste/box - or - yarn add @twilio-paste/core
`}</code></pre>
        <h4>{`Usage`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-jsx"
          }}>{`import {Box} from '@twilio-paste/box';

const Component = () => (
  <Box
    as="article"
    backgroundColor="colorBackgroundBody"
    padding="space60"
    _hover={{
      backgroundColor: "colorBackgroundPrimary"
      padding: "space70"
    }}
  >
    Foo
  </Box>
);
`}</code></pre>
        <h4>{`Props`}</h4>
        <p>{`All the regular HTML attributes (`}<inlineCode parentName="p">{`role`}</inlineCode>{`, `}<inlineCode parentName="p">{`aria-*`}</inlineCode>{`, `}<inlineCode parentName="p">{`type`}</inlineCode>{`, and so on) including the following custom props:`}</p>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`as?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'div'`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`display?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<CSS.DisplayProperty>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`overflow?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<CSS.OverflowProperty>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`backgroundColor?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['backgroundColors']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`width?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<WidthOptions>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`minWidth?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<MinWidthOptions>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`maxWidth?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<MaxWidthOptions>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`height?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<HeightOptions>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`minHeight?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<MinHeightOptions>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`maxHeight?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<MaxHeightOptions>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`size?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<WidthOptions>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`margin?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['space']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`marginTop?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['space']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`marginRight?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['space']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`marginBottom?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['space']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`marginLeft?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['space']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`padding?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['space']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`paddingTop?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['space']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`paddingRight?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['space']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`paddingBottom?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['space']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`paddingLeft?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['space']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderRadius?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['radii']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderTopLeftRadius?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['radii']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderTopRightRadius?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['radii']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderBottomRightRadius?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['radii']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderBottomLeftRadius?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['radii']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderWidth?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['borderWidths']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderTopWidth?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['borderWidths']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderRightWidth?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['borderWidths']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderBottomWidth?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['borderWidths']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderLeftWidth?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['borderWidths']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderColor?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['borderColors']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderTopColor?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['borderColors']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderRightColor?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['borderColors']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderBottomColor?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['borderColors']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderLeftColor?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['borderColors']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`borderStyle?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<CSS.BorderStyleProperty>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`boxshadow?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['shadows']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`zIndex?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['zIndices']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`cursor?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`CursorProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`content?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`string`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`appearance?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`AppearanceProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`animation?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`AnimationProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`transform?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`TransformProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`transformOrigin?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`TransformOriginProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`visibility?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`VisibilityProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`whiteSpace?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`WhiteSpaceProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`userSelect?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`UserSelectProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`pointerEvents?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`PointerEventsProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`boxSizing?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxSizingProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`resize?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`ResizeProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`transition?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`TransitionProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`listStyleType?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`ListStyleTypeProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`listStylePosition?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`ListStylePositionProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`listStyleImage?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`ListStyleImageProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`objectFit?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`ObjectFitProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`objectPosition?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`ObjectPositionProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`backgroundAttachment?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BackgroundAttachmentProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`outline?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`OutlineProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`float?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`FloatProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`willChange?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`WillChangeProperty`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`hover?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`active?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`focus?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`visited?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`even?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`odd?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`disabled?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`checked?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`mixed?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`selected?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`invalid?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`pressed?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`readOnly?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`first?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`last?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`expanded?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`grabbed?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`notFirst?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`notLast?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`groupHover?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`before?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`after?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`focusWithin?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`_`}{`placeholder?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`BoxProps`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
          </tbody>
        </table>
        <ChangelogRevealer mdxType="ChangelogRevealer">
  <Changelog mdxType="Changelog" />
        </ChangelogRevealer>
      </content>
    </contentwrapper>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      